import { useMemo, useState } from "react";
import {
  Calendar,
  type Event as RbcEvent,
  type View,
} from "react-big-calendar";
import { startOfWeek } from "date-fns";
import { de } from "date-fns/locale";

import { useCalendarFeed } from "@/hooks/useCalendar";
import {
  calendarFormats as buildFormats,
  calendarMessages,
  eventEnd,
  feedEventDetail,
  localizer,
} from "@/lib/calendar-locale";
import { type EventDetail, EventDetailDialog } from "@/components/EventDetailDialog";
import type { BookingCalendarEvent, MemberBookingItem, Room } from "@/types/api";

import "react-big-calendar/lib/css/react-big-calendar.css";
import "./booking-calendar.css";

const messages = calendarMessages("Keine Termine in diesem Zeitraum");

const calendarFormats = buildFormats("bis");

type MemberCalendarKind = "mine" | BookingCalendarEvent["kind"];

interface MemberCalendarEvent extends RbcEvent {
  kind: MemberCalendarKind;
  room_id: number;
  detail: EventDetail;
}

interface MemberBookingsCalendarProps {
  bookings: MemberBookingItem[];
  rooms: Room[];
}

function rangeForView(view: View, current: Date): { starts_at: string; ends_at: string } {
  const start = new Date(current);
  const end = new Date(current);
  if (view === "month") {
    start.setDate(1);
    end.setMonth(end.getMonth() + 1, 1);
  } else if (view === "day") {
    end.setDate(end.getDate() + 1);
  } else {
    const monday = startOfWeek(start, { locale: de });
    start.setTime(monday.getTime());
    end.setTime(monday.getTime());
    end.setDate(end.getDate() + 7);
  }
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);
  return { starts_at: start.toISOString(), ends_at: end.toISOString() };
}

export function MemberBookingsCalendar({ bookings, rooms }: MemberBookingsCalendarProps) {
  const [current, setCurrent] = useState<Date>(() => new Date());
  const [view, setView] = useState<View>("week");
  const [showOccupiedSlots, setShowOccupiedSlots] = useState<boolean>(true);
  const [detail, setDetail] = useState<EventDetail | null>(null);
  const range = useMemo(() => rangeForView(view, current), [view, current]);
  const calendarFeed = useCalendarFeed(range, showOccupiedSlots);

  const roomNamesById = useMemo(
    () => new Map(rooms.map((room) => [room.id, room.name])),
    [rooms],
  );

  const myBookingKeys = useMemo(
    () =>
      new Set(bookings.map((booking) => `${booking.room_id}:${booking.starts_at}:${booking.ends_at}`)),
    [bookings],
  );

  const events: MemberCalendarEvent[] = useMemo(() => {
    const mine = bookings.map((booking) => {
      const roomName = roomNamesById.get(booking.room_id) ?? `Raum #${booking.room_id}`;
      const start = new Date(booking.starts_at);
      const end = new Date(booking.ends_at);
      return {
        title: `Mein Termin · ${roomName}`,
        detail: { name: "Mein Termin", roomName, start, end },
        start,
        end: eventEnd(booking.ends_at),
        kind: "mine" as const,
        room_id: booking.room_id,
      };
    });

    if (!showOccupiedSlots) return mine;

    const occupied = (calendarFeed.data ?? [])
      .filter(
        (event) =>
          !myBookingKeys.has(`${event.room_id}:${event.starts_at}:${event.ends_at}`),
      )
      .map((event) => {
        // Kursname zuerst, damit sofort klar ist, was dort läuft.
        const name = event.title ?? (event.kind === "booked" ? "Gebucht" : "Gesperrt");
        const roomName = roomNamesById.get(event.room_id) ?? `Raum #${event.room_id}`;
        return {
          title: `${name} · ${roomName}`,
          detail: feedEventDetail(event, name, roomName),
          start: new Date(event.starts_at),
          end: eventEnd(event.ends_at),
          kind: event.kind,
          room_id: event.room_id,
        };
      });

    return [...mine, ...occupied];
  }, [bookings, calendarFeed.data, myBookingKeys, roomNamesById, showOccupiedSlots]);

  return (
    <section className="rounded-card border border-line bg-surface p-4 shadow-card md:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-h3 uppercase text-ink">Kalender</h2>
          <div className="mt-2 flex flex-wrap gap-3 text-caption text-ink-muted">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-success" />Meine Termine</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-primary" />Gebuchte Slots</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-surface-tan" />Sperrzeiten</span>
          </div>
        </div>
        <label className="inline-flex items-center gap-2 text-body-sm font-medium text-ink-soft">
          <input
            type="checkbox"
            checked={showOccupiedSlots}
            onChange={(event) => setShowOccupiedSlots(event.target.checked)}
            className="h-4 w-4 rounded border-line text-primary focus:ring-primary"
          />
          Belegte Slots anzeigen
        </label>
      </div>

      {calendarFeed.isLoading && showOccupiedSlots && (
        <p className="mb-3 text-body-sm text-ink-muted">Belegte Slots werden geladen...</p>
      )}

      <Calendar
        localizer={localizer}
        culture="de"
        messages={messages}
        formats={calendarFormats}
        events={events}
        view={view}
        onView={setView}
        date={current}
        onNavigate={setCurrent}
        views={["week", "day", "month", "agenda"]}
        // Events über Mitternacht (Partys bis 02:00) ins Raster statt in die Ganztags-Zeile.
        showMultiDayTimes
        step={30}
        timeslots={2}
        min={new Date(1970, 0, 1, 7, 0)}
        max={new Date(1970, 0, 1, 23, 0)}
        popup
        onSelectEvent={(event) => setDetail((event as MemberCalendarEvent).detail)}
        eventPropGetter={(event) => ({
          className: `rbc-event--member-${(event as MemberCalendarEvent).kind}`,
        })}
        style={{ height: 620 }}
      />
      <EventDetailDialog detail={detail} onClose={() => setDetail(null)} />
    </section>
  );
}